import { useState, type FormEvent } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Truck, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useOrder, useOrderItems } from "@/hooks/useOrders";
import {
  formatOre,
  formatOrderDateLong,
  markOrderShipped,
  STATUS_LABEL,
  updateOrderStatus,
  type OrderRow,
  type OrderStatus,
} from "@/lib/orders";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import { handleApiError } from "@/lib/errors";
import { toast } from "@/components/ui/sonner";

function statusBadge(status: OrderStatus) {
  const label = STATUS_LABEL[status];
  switch (status) {
    case "PAID":
      return <Badge className="bg-blue-500/15 text-blue-700 hover:bg-blue-500/20">{label}</Badge>;
    case "SHIPPED":
      return <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/20">{label}</Badge>;
    case "DELIVERED":
      return <Badge className="bg-green-500/15 text-green-700 hover:bg-green-500/20">{label}</Badge>;
    case "CANCELLED":
    case "REFUNDED":
      return <Badge variant="secondary">{label}</Badge>;
    default:
      return <Badge variant="outline">{label}</Badge>;
  }
}

interface ProductSnapshot {
  // Kustom-webhook writes `name` and `reference`. Older formats may have
  // `title`/`variant_title`. Try both.
  name?: string;
  reference?: string;
  title?: string;
  variant_title?: string;
  [k: string]: unknown;
}

function snapshotTitle(snapshot: unknown): string {
  if (snapshot && typeof snapshot === "object") {
    const s = snapshot as ProductSnapshot;
    if (s.name) return s.name;
    const parts = [s.title, s.variant_title].filter(Boolean);
    if (parts.length) return parts.join(" — ");
    if (s.reference) return s.reference;
  }
  return "(produkt)";
}

/**
 * Kustom-webhook lagrer alle order_lines, inkludert shipping. Vi har
 * shipping_amount_ore på `orders` separat, så vi filtrerer bort shipping-
 * linjer her for å unngå dobbeltvisning. Samme matcher som webhook bruker
 * internt.
 */
function isShippingLine(snapshot: unknown): boolean {
  if (!snapshot || typeof snapshot !== "object") return false;
  const ref = (snapshot as ProductSnapshot).reference ?? "";
  return /^POSTEN|^PICKUP$/i.test(ref);
}

interface ShipDialogProps {
  order: OrderRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ShipDialog = ({ order, open, onOpenChange }: ShipDialogProps) => {
  const queryClient = useQueryClient();
  const { session } = useAdminAuth();
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier] = useState("Posten");
  const [trackingUrl, setTrackingUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!session?.access_token) {
      toast.error("Sesjonen din er utløpt. Logg inn på nytt.");
      return;
    }
    setSubmitting(true);
    try {
      await markOrderShipped(
        {
          order_id: order.id,
          tracking_number: trackingNumber.trim(),
          tracking_carrier: carrier.trim() || undefined,
          tracking_url: trackingUrl.trim() || undefined,
        },
        session.access_token
      );
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Ordre markert som sendt. Bekreftelse sendt til kunde.");
      onOpenChange(false);
    } catch (err) {
      handleApiError(err, "Kunne ikke markere som sendt.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Marker som sendt</DialogTitle>
          <DialogDescription>
            Kunden får e-post med sporingsinformasjon når du lagrer.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="carrier">Fraktselskap</Label>
            <Input
              id="carrier"
              value={carrier}
              onChange={(e) => setCarrier(e.target.value)}
              placeholder="Posten, Bring, …"
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="trackingNumber">Sporingsnummer *</Label>
            <Input
              id="trackingNumber"
              required
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="trackingUrl">Sporingslenke (valgfritt)</Label>
            <Input
              id="trackingUrl"
              type="url"
              value={trackingUrl}
              onChange={(e) => setTrackingUrl(e.target.value)}
              placeholder="https://sporing.posten.no/..."
              disabled={submitting}
            />
          </div>
          <Button type="submit" className="w-full" disabled={submitting}>
            {submitting ? "Sender …" : "Marker som sendt"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AdminOrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: order, isLoading } = useOrder(id);
  const { data: items } = useOrderItems(id);
  const [shipOpen, setShipOpen] = useState(false);

  async function setDelivered() {
    if (!order) return;
    if (!confirm("Marker ordren som levert?")) return;
    try {
      await updateOrderStatus(order.id, "DELIVERED");
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      toast.success("Ordre markert som levert.");
    } catch (err) {
      handleApiError(err, "Kunne ikke oppdatere status.");
    }
  }

  if (isLoading) return <p className="text-sm text-muted-foreground">Laster …</p>;
  if (!order) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-destructive">Fant ikke ordren.</p>
        <Button variant="ghost" onClick={() => navigate("/admin/orders")}>Tilbake</Button>
      </div>
    );
  }

  const orderRef = order.kco_order_id ?? order.id.slice(0, 8);
  // Kustom lagrer adresse-felter på sitt eget format (street_address,
  // postal_code, city, country, given_name, family_name). Hold legacy line1/
  // line2 for ordrer fra tidligere format.
  const address = order.shipping_address as
    | {
        street_address?: string;
        line1?: string;
        line2?: string;
        postal_code?: string;
        city?: string;
        country?: string;
        given_name?: string;
        family_name?: string;
      }
    | null;
  const recipient = address
    ? [address.given_name, address.family_name].filter(Boolean).join(" ")
    : "";
  const street = address?.street_address ?? address?.line1 ?? "";
  const postalLine = address
    ? [address.postal_code, address.city].filter(Boolean).join(" ")
    : "";
  const countryLine = address?.country ? address.country.toUpperCase() : "";

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild className="-ml-3">
          <Link to="/admin/orders">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Tilbake
          </Link>
        </Button>
        <div className="mt-2 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold">Ordre {orderRef}</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Mottatt {formatOrderDateLong(order.created_at)}
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            {statusBadge(order.status)}
            <p className="text-2xl font-semibold">{formatOre(order.amount_total_ore)}</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-lg border bg-card p-5">
          <h2 className="text-sm font-medium text-muted-foreground">Kunde</h2>
          <div className="mt-2 space-y-1 text-sm">
            <p className="font-medium">{order.customer_name ?? "—"}</p>
            {order.customer_email && (
              <a href={`mailto:${order.customer_email}`} className="block text-primary hover:underline">
                {order.customer_email}
              </a>
            )}
            {order.customer_phone && (
              <a href={`tel:${order.customer_phone}`} className="block text-muted-foreground hover:underline">
                {order.customer_phone}
              </a>
            )}
          </div>
        </div>
        <div className="rounded-lg border bg-card p-5">
          <h2 className="text-sm font-medium text-muted-foreground">Levering</h2>
          <div className="mt-2 space-y-1 text-sm">
            <p>
              {order.shipping_method === "POSTEN"
                ? "Posten"
                : order.shipping_method === "PICKUP"
                ? "Henting hos Håp Naturlab"
                : "—"}
            </p>
            {address && (street || postalLine) && (
              <address className="not-italic text-muted-foreground">
                {recipient && <div className="text-foreground">{recipient}</div>}
                {street && <div>{street}</div>}
                {address.line2 && <div>{address.line2}</div>}
                {postalLine && <div>{postalLine}</div>}
                {countryLine && countryLine !== "NO" && <div>{countryLine}</div>}
              </address>
            )}
            {order.tracking_number && (
              <p className="pt-2 text-xs text-muted-foreground">
                {order.tracking_carrier ? `${order.tracking_carrier} · ` : ""}
                Sporing:{" "}
                {order.tracking_url ? (
                  <a href={order.tracking_url} target="_blank" rel="noreferrer" className="font-mono text-primary hover:underline">
                    {order.tracking_number}
                  </a>
                ) : (
                  <span className="font-mono">{order.tracking_number}</span>
                )}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <div className="border-b p-5">
          <h2 className="text-sm font-medium text-muted-foreground">Varer</h2>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/20 text-left">
            <tr>
              <th className="px-4 py-2 font-medium text-muted-foreground">Produkt</th>
              <th className="px-4 py-2 font-medium text-muted-foreground">Antall</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Stykkpris</th>
              <th className="px-4 py-2 text-right font-medium text-muted-foreground">Sum</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {(items ?? [])
              .filter((it) => !isShippingLine(it.product_snapshot))
              .map((it) => (
                <tr key={it.id}>
                  <td className="px-4 py-2">{snapshotTitle(it.product_snapshot)}</td>
                  <td className="px-4 py-2">{it.quantity}</td>
                  <td className="px-4 py-2 text-right">{formatOre(it.unit_price_ore)}</td>
                  <td className="px-4 py-2 text-right">{formatOre(it.total_ore)}</td>
                </tr>
              ))}
          </tbody>
          <tfoot className="border-t">
            <tr>
              <td colSpan={3} className="px-4 py-2 text-right text-muted-foreground">Frakt</td>
              <td className="px-4 py-2 text-right">{formatOre(order.shipping_amount_ore)}</td>
            </tr>
            <tr>
              <td colSpan={3} className="px-4 py-2 text-right font-medium">Totalt</td>
              <td className="px-4 py-2 text-right font-semibold">{formatOre(order.amount_total_ore)}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Separator />

      <div className="flex flex-wrap gap-3">
        {order.status === "PAID" && (
          <Button onClick={() => setShipOpen(true)}>
            <Truck className="mr-2 h-4 w-4" />
            Marker som sendt
          </Button>
        )}
        {order.status === "SHIPPED" && (
          <Button onClick={setDelivered}>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            Marker som levert
          </Button>
        )}
      </div>

      <ShipDialog order={order} open={shipOpen} onOpenChange={setShipOpen} />
    </div>
  );
};

export default AdminOrderDetail;
